// ScoreDetailsModal.js — right-side slide-in drawer with a hero
// scoreline, first/latest occurrence cards, a Scorigami badge (when
// applicable), and a "Tweet this" share button.
//
// Kept under the original filename to keep imports stable from
// ScoresHeatmap.js.

import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import BoltIcon from "@mui/icons-material/Bolt";
import DownloadIcon from "@mui/icons-material/Download";
import XIcon from "@mui/icons-material/X";
import { toPng } from "html-to-image";
import { BRAND } from "../../theme";
import {
  AXIS_LABELS,
  HOME_AWAY,
  formatDate,
  isCurrentSeason,
  occurrenceLine,
  orientOccurrence,
  parseScoreString,
  shortSeasonLabel,
  toSeasonId,
  toWinnerFirst,
} from "./utils";
import { Link as RouterLink } from "react-router-dom";
import { LOGO_CELLS } from "../AppHeader";
import { trackEvent } from "../../analytics";
import ScoreSparkline from "./ScoreSparkline";

const ITEMS_PER_PAGE = 10;
const SITE_URL = "https://premierleaguescorigami.com";

function useCountUp(target, { duration = 700, active } = {}) {
  const [val, setVal] = useState(0);
  const rafRef = useRef();
  useEffect(() => {
    if (!active || !target) {
      setVal(target || 0);
      return;
    }
    const start = performance.now();
    const from = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(from + (target - from) * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration, active]);
  return val;
}

function OccurrenceCard({ label, data, axes }) {
  if (!data) return null;
  const o = orientOccurrence(data, axes);
  const leftName = o.leftTeam?.name || "—";
  const rightName = o.rightTeam?.name || "—";
  return (
    <Box
      sx={{
        p: 1.5,
        mb: 1.25,
        borderRadius: 2,
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: BRAND.violet,
          fontWeight: 700,
          letterSpacing: "0.08em",
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{ display: "block", color: BRAND.textSecondary, mb: 0.75 }}
      >
        {formatDate(data.utcDate)}
      </Typography>
      <Box display="flex" alignItems="center" gap={1}>
        <Avatar
          src={o.leftTeam?.image}
          alt={leftName}
          sx={{ width: 26, height: 26 }}
        />
        <Typography variant="body2" color="#fff" sx={{ fontWeight: 600 }} noWrap>
          {leftName} {o.leftScore} – {o.rightScore} {rightName}
        </Typography>
        <Avatar
          src={o.rightTeam?.image}
          alt={rightName}
          sx={{ width: 26, height: 26 }}
        />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Share text and image
// ---------------------------------------------------------------------------
const X_INTENT = "https://x.com/intent/tweet";
const X_VIA = "pl_gami"; // X appends " via @pl_gami"
const TWEET_MAX = 280;
// X counts any link as 23 characters.
const tweetLength = (text) => text.replace(SITE_URL, "x".repeat(23)).length + ` via @${X_VIA}`.length;
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const NUMBER_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
const spellSmall = (n) => NUMBER_WORDS[n] || n.toLocaleString();

// "home win" / "away win" / "draw" in the home–away view; "win" / "draw"
// in winner–loser, where venue doesn't count.
function resultKind(key, axes) {
  const p = parseScoreString(key);
  if (!p || p.a === p.b) return "draw";
  if (axes !== HOME_AWAY) return "win";
  return p.a > p.b ? "home win" : "away win";
}

// "a 3–1 home win", "an 8–0 win", "a 2–2 draw".
function resultPhrase(key, axes) {
  const score = key.replace("-", "–");
  return `${/^(8|11|18)–/.test(score) ? "an" : "a"} ${score} ${resultKind(key, axes)}`;
}

// Where a count applies: all time, a finished season, or the current one.
function countScope(isAllSeasons, season) {
  if (isAllSeasons) return { where: "in Premier League history", verb: "has happened", label: "All-time" };
  const short = shortSeasonLabel(season);
  const current = isCurrentSeason(season);
  return {
    where: `in the ${short} Premier League season${current ? " so far" : ""}`,
    verb: current ? "has happened" : "happened",
    label: `${short} season`,
  };
}

function buildTweet({ selectedScore, count, firstOccurrence, latestOccurrence, isScorigami, isAllSeasons, season, axes }) {
  const phrase = resultPhrase(selectedScore, axes);
  const { where, verb } = countScope(isAllSeasons, season);
  let headline;
  if (isScorigami) headline = `Scorigami: only one Premier League match has ever ended in ${phrase}.`;
  else if (isAllSeasons && count <= 5) headline = `Only ${spellSmall(count)} Premier League matches have ever ended in ${phrase}.`;
  else headline = `${capitalize(phrase)} ${verb} ${count === 1 ? "once" : `${count.toLocaleString()} times`} ${where}.`;

  const match = (occ) => `${occurrenceLine(occ, axes)}, ${formatDate(occ.utcDate)}`;
  let details = [];
  if (firstOccurrence) {
    const onlyOne = count === 1 || !latestOccurrence || latestOccurrence.utcDate === firstOccurrence.utcDate;
    details = onlyOne
      ? [match(firstOccurrence)]
      : [`First: ${match(firstOccurrence)}`, `Most recent: ${match(latestOccurrence)}`];
  }
  const footer = `Explore every scoreline: ${SITE_URL}\n${isScorigami ? "#PremierLeague #Scorigami" : "#PremierLeague"}`;
  const compose = (lines) => [headline, lines.join("\n"), footer].filter(Boolean).join("\n\n");

  // Long team names can overflow 280: drop match lines, oldest first.
  let text = compose(details);
  while (tweetLength(text) > TWEET_MAX && details.length) {
    details = details.slice(1);
    text = compose(details);
  }
  return text;
}

const CARD_FONT = '"Poppins","Helvetica","Arial",sans-serif';

// Poppins as data: URLs for html-to-image. Its own font inlining reads the
// Google Fonts stylesheet's cssRules, which the browser blocks cross-origin
// (it recovers, but logs errors), so fetch the CSS and font files directly.
// Latin subset only; fetched once per page load.
let cardFontCss = null;
function loadCardFontCss() {
  if (!cardFontCss) {
    cardFontCss = (async () => {
      const href = document.querySelector('link[href*="fonts.googleapis.com"]')?.href;
      if (!href) return "";
      const css = await (await fetch(href)).text();
      const faces = css.split("@font-face").filter((f) => f.includes("U+0000-00FF"));
      const inlined = await Promise.all(
        faces.map(async (face) => {
          const url = face.match(/url\((https:[^)]+)\)/)?.[1];
          if (!url) return "";
          const blob = await (await fetch(url)).blob();
          const data = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.onerror = reject;
            r.readAsDataURL(blob);
          });
          return `@font-face${face.replace(url, data)}`;
        })
      );
      return inlined.join("\n");
    })().catch((err) => {
      console.warn("Share image font load failed; using a system font:", err);
      cardFontCss = null;
      return "";
    });
  }
  return cardFontCss;
}
const CARD_MUTED = "#8d90a0";

// Hidden 1080×1080 card rendered off-screen and snapshotted to PNG by
// html-to-image. Inline styles only, and no gradient text: PNG export doesn't
// capture WebkitBackgroundClip reliably.
const ShareCard = React.forwardRef(function ShareCard(
  { selectedScore, count, isScorigami, isAllSeasons, season, firstOccurrence, latestOccurrence, axes },
  ref
) {
  const p = parseScoreString(selectedScore) || { a: "", b: "" };
  const scope = countScope(isAllSeasons, season);
  const heroSize = Math.max(p.a, p.b) >= 10 ? 190 : 230;
  const sides = axes === HOME_AWAY ? ["Home", "Away"] : [null, null];
  const rows = !firstOccurrence
    ? []
    : latestOccurrence
      ? [["First", firstOccurrence], ["Most recent", latestOccurrence]]
      : [[count === 1 ? "The match" : "First", firstOccurrence]];

  const eyebrow = {
    fontSize: 17,
    fontWeight: 700,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    lineHeight: 1,
  };
  const logoCell = (56 - 2 * 4) / 3;

  // The side label hangs below the number, outside the row's height, so the
  // dash centres on the digits.
  const Num = ({ value, side }) => (
    <div style={{ position: "relative", display: "flex", justifyContent: "center" }}>
      <div style={{ fontSize: heroSize, fontWeight: 900, lineHeight: 1, letterSpacing: "-0.03em" }}>{value}</div>
      {side && (
        <div style={{ ...eyebrow, color: CARD_MUTED, position: "absolute", top: "100%", marginTop: 6 }}>{side}</div>
      )}
    </div>
  );

  return (
    <div
      ref={ref}
      style={{
        width: 1080,
        height: 1080,
        padding: "64px 72px 56px",
        boxSizing: "border-box",
        background:
          "radial-gradient(900px 620px at 50% 40%, rgba(111,93,245,0.22), transparent 70%), linear-gradient(180deg, #15151c 0%, #0c0c10 100%)",
        fontFamily: CARD_FONT,
        color: "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Brand lockup and what the numbers cover */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <svg width="56" height="56" viewBox="0 0 56 56" aria-hidden>
            {LOGO_CELLS.map((row, r) =>
              row.map((fill, c) => (
                <rect
                  key={`${r}-${c}`}
                  x={c * (logoCell + 4)}
                  y={r * (logoCell + 4)}
                  width={logoCell}
                  height={logoCell}
                  rx={logoCell * 0.22}
                  fill={fill}
                />
              ))
            )}
          </svg>
          <div style={{ display: "inline-flex", flexDirection: "column" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                color: "#b0b0b0",
                fontSize: 20,
                fontWeight: 700,
                lineHeight: 1,
                marginBottom: 7,
              }}
            >
              {[..."PREMIER LEAGUE"].map((ch, i) => (ch === " " ? <span key={i} /> : <span key={i}>{ch}</span>))}
            </div>
            <div style={{ fontSize: 40, fontWeight: 900, lineHeight: 1, letterSpacing: "-0.01em" }}>SCORIGAMI</div>
          </div>
        </div>
        <div
          style={{
            ...eyebrow,
            fontSize: 16,
            letterSpacing: "0.12em",
            color: "#d7dae3",
            padding: "12px 20px",
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.14)",
            background: "rgba(255,255,255,0.04)",
            whiteSpace: "nowrap",
          }}
        >
          {scope.label} · {AXIS_LABELS[axes].short}
        </div>
      </div>

      {/* The scoreline */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        {isScorigami && (
          <div
            style={{
              ...eyebrow,
              padding: "12px 22px",
              borderRadius: 999,
              background: "linear-gradient(90deg, #39d0ff, #6f5df5)",
              marginBottom: 22,
            }}
          >
            Scorigami
          </div>
        )}
        <div style={{ ...eyebrow, fontSize: 20, color: BRAND.cyan, marginBottom: 10 }}>{resultKind(selectedScore, axes)}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 44 }}>
          <Num value={p.a} side={sides[0]} />
          <div
            style={{
              width: heroSize * 0.28,
              height: heroSize * 0.075,
              borderRadius: 999,
              background: "rgba(255,255,255,0.35)",
              marginTop: heroSize * 0.04,
            }}
          />
          <Num value={p.b} side={sides[1]} />
        </div>
        <div style={{ marginTop: sides[0] ? 64 : 30, fontSize: 34, fontWeight: 500, color: "#b0b0b0", textAlign: "center" }}>
          <span style={{ color: "#fff", fontWeight: 700 }}>{count === 1 ? "Once" : `${count.toLocaleString()} times`}</span>{" "}
          {scope.where}
        </div>
      </div>

      {/* First and most recent match */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        {rows.map(([label, occ]) => (
          <div
            key={label}
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 28,
              padding: "22px 0",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ ...eyebrow, fontSize: 15, color: BRAND.cyan, marginBottom: 12 }}>{label}</div>
              <div style={{ fontSize: 32, fontWeight: 700, lineHeight: 1.15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {occurrenceLine(occ, axes)}
              </div>
            </div>
            <div style={{ color: "#b0b0b0", fontSize: 22, fontWeight: 500, whiteSpace: "nowrap", paddingBottom: 3 }}>
              {formatDate(occ.utcDate)}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 26, fontSize: 20, fontWeight: 500, color: CARD_MUTED }}>
        <span style={{ color: "#e6e8ee", fontWeight: 700 }}>premierleaguescorigami.com</span>
        <span>@pl_gami on X</span>
      </div>
    </div>
  );
});

export default function ScoreDetailsModal({
  selectedScore,
  onClose,
  isAllSeasons,
  season,
  axes,
  scoreSummaryMap,
  scoreMap,
  firstOccurrence,
  latestOccurrence,
  showLatest,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(1);
  const [showDateMap, setShowDateMap] = useState({});

  useEffect(() => {
    setPage(1);
    setShowDateMap({});
  }, [selectedScore]);

  useEffect(() => {
    if (selectedScore) {
      trackEvent("select_content", {
        content_type: "scoreline",
        item_id: selectedScore,
        axes,
        season: isAllSeasons ? "all" : season,
      });
    }
  }, [selectedScore, axes, isAllSeasons, season]);
  const trackShare = (method) =>
    trackEvent("share", { method, content_type: "scoreline", item_id: selectedScore });

  const open = !!selectedScore;

  const count = selectedScore
    ? isAllSeasons
      ? scoreSummaryMap[selectedScore]?.count || 0
      : scoreMap[selectedScore]?.length || 0
    : 0;

  const animatedCount = useCountUp(count, { active: open, duration: 700 });
  const matches = !isAllSeasons && selectedScore ? scoreMap[selectedScore] || [] : [];
  const isScorigami = isAllSeasons && count === 1;

  // Ref to the share card (hidden) used by the "Save image" button.
  const shareCardRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const handleSaveImage = async () => {
    if (!shareCardRef.current) return;
    try {
      setSaving(true);
      const dataUrl = await toPng(shareCardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#0f0f13",
        fontEmbedCSS: await loadCardFontCss(),
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `premier-league-scorigami-${selectedScore}${isAllSeasons ? "" : `-${toSeasonId(season)}`}.png`;
      trackShare("image");
      a.click();
    } catch (err) {
      console.error("Share image failed:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : { sm: 440, md: 480 },
          maxHeight: isMobile ? "88vh" : "100%",
          overflowY: "auto",
          background:
            "linear-gradient(180deg, rgba(20,20,26,0.98) 0%, rgba(15,15,19,0.98) 100%)",
          borderLeft: isMobile ? "none" : `1px solid ${BRAND.divider}`,
          borderTopLeftRadius: isMobile ? 16 : 0,
          borderTopRightRadius: isMobile ? 16 : 0,
          backdropFilter: "blur(18px)",
          boxShadow: "-10px 0 40px rgba(0,0,0,0.6)",
          color: "#fff",
        },
      }}
    >
      {!selectedScore ? null : (
        <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 0 }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: BRAND.cyan,
                  letterSpacing: "0.14em",
                  fontWeight: 700,
                }}
              >
                {AXIS_LABELS[axes].short}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 48, sm: 64 },
                  fontWeight: 800,
                  lineHeight: 1,
                  background: BRAND.gradient,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-0.03em",
                }}
              >
                {selectedScore?.replace("-", " – ")}
              </Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ color: "#fff" }} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Count + scorigami badge */}
          <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700 }}>
              {animatedCount.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ color: BRAND.textSecondary }}>
              {isAllSeasons
                ? `time${count === 1 ? "" : "s"} in Premier League history`
                : `time${count === 1 ? "" : "s"} this season`}
            </Typography>
            {isScorigami && (
              <Chip
                icon={<BoltIcon sx={{ color: "#fff !important" }} />}
                label="Scorigami"
                size="small"
                sx={{
                  ml: "auto",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  color: "#fff",
                  background: BRAND.gradient,
                  boxShadow: "0 0 14px rgba(111,93,245,0.5)",
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              my: 2,
              height: 1,
              background:
                "linear-gradient(90deg, transparent, rgba(111,93,245,0.5), transparent)",
            }}
          />

          {/* Body */}
          <Box>
            <OccurrenceCard label="First occurrence" data={firstOccurrence} axes={axes} />
            {showLatest && (
              <OccurrenceCard label="Most recent" data={latestOccurrence} axes={axes} />
            )}

            {isAllSeasons && (
              <ScoreSparkline
                selectedScore={selectedScore}
                axes={axes}
                seasonMatches={null}
              />
            )}

            {!isAllSeasons && matches.length > 1 && (
              <Box mt={1}>
                <Typography
                  variant="overline"
                  sx={{
                    color: BRAND.violet,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                  }}
                >
                  All matches this season
                </Typography>
                {matches.slice(0, page * ITEMS_PER_PAGE).map((match, idx) => {
                  const o = orientOccurrence(match, axes);
                  return (
                    <Box key={idx} sx={{ mb: 1.2 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={o.leftTeam?.image} alt={o.leftTeam?.name} sx={{ width: 22, height: 22 }} />
                        <Typography variant="body2" color="#fff" noWrap>
                          {o.leftTeam?.name} {o.leftScore} – {o.rightScore}{" "}
                          {o.rightTeam?.name}
                        </Typography>
                        <Avatar src={o.rightTeam?.image} alt={o.rightTeam?.name} sx={{ width: 22, height: 22 }} />
                        <IconButton
                          size="small"
                          sx={{ p: 0, color: BRAND.violet }}
                          onClick={() =>
                            setShowDateMap((prev) => ({ ...prev, [idx]: !prev[idx] }))
                          }
                          aria-label="Show date"
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {showDateMap[idx] && (
                        <Typography
                          variant="caption"
                          color={BRAND.textSecondary}
                          sx={{ ml: 4, display: "block" }}
                        >
                          {formatDate(match.utcDate)}
                        </Typography>
                      )}
                    </Box>
                  );
                })}

                {matches.length > page * ITEMS_PER_PAGE && (
                  <Button
                    onClick={() => setPage((p) => p + 1)}
                    variant="outlined"
                    size="small"
                    sx={{
                      mt: 1,
                      borderColor: BRAND.divider,
                      color: BRAND.cyan,
                      "&:hover": { borderColor: BRAND.cyan },
                    }}
                  >
                    Show more
                  </Button>
                )}
              </Box>
            )}
          </Box>

          <Button
            component={RouterLink}
            to={`/scorelines/${toWinnerFirst(selectedScore)}`}
            onClick={onClose}
            sx={{
              mt: 2,
              alignSelf: "flex-start",
              px: 0,
              textTransform: "none",
              fontWeight: 700,
              color: BRAND.cyan,
              "&:hover": { background: "transparent", textDecoration: "underline" },
            }}
          >
            Every {toWinnerFirst(selectedScore).replace("-", "–")} in Premier League history →
          </Button>

          {/* Share */}
          <Box sx={{ pt: 2, mt: 2, borderTop: `1px solid ${BRAND.divider}` }}>
            <Stack direction="row" spacing={1} alignItems="stretch">
              <Button
                fullWidth
                variant="contained"
                startIcon={<XIcon sx={{ fontSize: 16 }} />}
                href={`${X_INTENT}?text=${encodeURIComponent(
                  buildTweet({
                    selectedScore,
                    count,
                    firstOccurrence,
                    latestOccurrence,
                    isScorigami,
                    isAllSeasons,
                    season,
                    axes,
                  })
                )}&via=${X_VIA}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackShare("x")}
                sx={{
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  color: "#fff",
                  background: BRAND.gradient,
                  "&:hover": {
                    background: BRAND.gradientReverse,
                    boxShadow: "0 6px 20px rgba(111,93,245,0.4)",
                  },
                }}
              >
                Share on X
              </Button>
              <IconButton
                onClick={handleSaveImage}
                disabled={saving}
                aria-label="Save as image"
                title={saving ? "Saving…" : "Save as image"}
                sx={{
                  flexShrink: 0,
                  width: 44,
                  color: BRAND.cyan,
                  border: `1px solid ${BRAND.divider}`,
                  borderRadius: 1,
                  "&:hover": {
                    borderColor: BRAND.cyan,
                    background: "rgba(57,208,255,0.08)",
                  },
                }}
              >
                <DownloadIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Stack>
          </Box>

          {/* Hidden share card rendered off-screen for PNG export */}
          <Box
            sx={{
              position: "fixed",
              top: "-10000px",
              left: "-10000px",
              pointerEvents: "none",
            }}
            aria-hidden
          >
            <ShareCard
              ref={shareCardRef}
              selectedScore={selectedScore}
              count={count}
              isScorigami={isScorigami}
              isAllSeasons={isAllSeasons}
              season={season}
              axes={axes}
              firstOccurrence={firstOccurrence}
              latestOccurrence={showLatest ? latestOccurrence : null}
            />
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
